import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  MapPin, Car, User, Phone, Navigation, Clock, 
  RefreshCw, X, Zap, ArrowRight
} from "lucide-react";

export default function LiveVehicleMap({ onAssignRide, onReassignRide }) {
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [nearbyPickups, setNearbyPickups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    fetchVehicles();
    const interval = setInterval(fetchVehicles, 10000); // Update every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await apiClient.get("/dispatch/live-vehicles");
      setVehicles(response.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error("Failed to fetch vehicles:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNearbyPickups = async (driverId) => {
    try {
      const response = await apiClient.get(`/dispatch/nearby-pickups/${driverId}`);
      setNearbyPickups(response.data);
    } catch (error) {
      toast.error("Failed to fetch nearby pickups");
    }
  };

  const handleSelectVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    if (vehicle.is_available) {
      fetchNearbyPickups(vehicle.id);
    } else {
      setNearbyPickups([]);
    }
  };

  const handleQuickAssign = async (rideId, driverId) => {
    try {
      await apiClient.post("/dispatch/quick-schedule", {
        ride_id: rideId,
        driver_id: driverId
      });
      toast.success("Ride scheduled instantly!");
      fetchVehicles();
      setNearbyPickups([]);
      setSelectedVehicle(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to schedule ride");
    }
  };

  const openNavigation = (location, provider = "google") => {
    if (!location) return;
    
    const { latitude, longitude } = location;
    let url;
    
    if (provider === "waze") {
      url = `https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes`;
    } else {
      url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
    }
    
    window.open(url, "_blank");
  };

  const availableVehicles = vehicles.filter(v => v.is_available);
  const activeVehicles = vehicles.filter(v => !v.is_available);

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      {/* Vehicle List */}
      <div className="lg:col-span-2 space-y-4">
        {/* Stats Bar */}
        <div className="flex items-center justify-between bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm font-medium">{availableVehicles.length} Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-cyan-500 animate-pulse"></div>
              <span className="text-sm font-medium">{activeVehicles.length} On Ride</span>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={fetchVehicles}
            className="text-slate-500"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            {lastUpdate && `Updated ${Math.round((new Date() - lastUpdate) / 1000)}s ago`}
          </Button>
        </div>

        {/* Live Map Placeholder - In production, integrate with Google Maps */}
        <Card className="bg-gradient-to-br from-slate-800 to-cyan-900 text-white overflow-hidden">
          <CardContent className="p-0 relative" style={{ minHeight: "400px" }}>
            {/* Mock Map Background */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundSize: "30px 30px"
              }}></div>
            </div>

            {/* Vehicle Markers */}
            <div className="relative p-6">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Navigation className="w-5 h-5 text-cyan-400" />
                Live Fleet View
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {vehicles.map((vehicle) => (
                  <button
                    key={vehicle.id}
                    onClick={() => handleSelectVehicle(vehicle)}
                    className={`p-3 rounded-xl text-left transition-all ${
                      selectedVehicle?.id === vehicle.id
                        ? "bg-cyan-500 shadow-lg scale-105"
                        : vehicle.is_available
                        ? "bg-white/10 hover:bg-white/20"
                        : "bg-orange-500/30 hover:bg-orange-500/40"
                    }`}
                    data-testid={`vehicle-marker-${vehicle.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Car className={`w-4 h-4 ${vehicle.is_available ? "text-green-400" : "text-orange-400"}`} />
                      <span className="font-medium text-sm truncate">{vehicle.name}</span>
                    </div>
                    <div className="text-xs text-white/70">
                      {vehicle.is_available ? "Available" : "On Ride"}
                    </div>
                    {vehicle.current_location && (
                      <div className="text-xs text-cyan-300 mt-1">
                        {vehicle.current_location.speed || 0} km/h
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {vehicles.length === 0 && (
                <div className="text-center py-12 text-white/60">
                  <Car className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No drivers registered yet</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Selected Vehicle Details */}
      <div className="space-y-4">
        {selectedVehicle ? (
          <>
            <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50 to-white">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Car className="w-5 h-5 text-cyan-600" />
                    {selectedVehicle.name}
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedVehicle(null)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <Badge className={selectedVehicle.is_available ? "bg-green-500" : "bg-orange-500"}>
                  {selectedVehicle.is_available ? "Available" : "On Ride"}
                </Badge>

                {selectedVehicle.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <a href={`tel:${selectedVehicle.phone}`} className="text-cyan-600 hover:underline">
                      {selectedVehicle.phone}
                    </a>
                  </div>
                )}

                {selectedVehicle.vehicle_make && (
                  <div className="text-sm text-slate-600">
                    {[selectedVehicle.vehicle_color, selectedVehicle.vehicle_make, selectedVehicle.vehicle_model]
                      .filter(Boolean).join(" ")}
                    {selectedVehicle.vehicle_plate && ` • ${selectedVehicle.vehicle_plate}`}
                  </div>
                )}

                {selectedVehicle.current_location && (
                  <div className="flex gap-2 pt-2">
                    <Button 
                      size="sm" 
                      className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500"
                      onClick={() => openNavigation(selectedVehicle.current_location, "google")}
                    >
                      <Navigation className="w-4 h-4 mr-1" />
                      Google Maps
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="flex-1"
                      onClick={() => openNavigation(selectedVehicle.current_location, "waze")}
                    >
                      Waze
                    </Button>
                  </div>
                )}

                {/* Active Ride Info */}
                {selectedVehicle.active_ride && (
                  <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <h4 className="font-semibold text-orange-800 text-sm mb-2">Current Ride</h4>
                    <p className="text-sm text-slate-700">{selectedVehicle.active_ride.client_name}</p>
                    <p className="text-xs text-slate-500">{selectedVehicle.active_ride.pickup_locations?.[0]?.address}</p>
                    <Badge className="mt-2 bg-orange-100 text-orange-700">
                      {selectedVehicle.active_ride.status}
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Nearby Pickups - Reduce Deadhead */}
            {selectedVehicle.is_available && nearbyPickups.length > 0 && (
              <Card className="border-purple-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-purple-700">
                    <Zap className="w-4 h-4" />
                    Nearby Pickups (Reduce Deadhead)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {nearbyPickups.slice(0, 5).map((ride) => (
                    <div 
                      key={ride.id}
                      className="p-2 bg-purple-50 rounded-lg border border-purple-100 hover:bg-purple-100 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{ride.client_name}</p>
                          <p className="text-xs text-slate-500">{ride.pickup_time} • {ride.pickup_date}</p>
                          {ride.distance_estimate && (
                            <p className="text-xs text-purple-600">~{ride.distance_estimate} km away</p>
                          )}
                        </div>
                        <Button 
                          size="sm"
                          className="bg-purple-500 hover:bg-purple-600"
                          onClick={() => handleQuickAssign(ride.id, selectedVehicle.id)}
                          data-testid={`quick-assign-${ride.id}`}
                        >
                          <Zap className="w-3 h-3 mr-1" />
                          Assign
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center text-slate-500">
              <MapPin className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Select a vehicle</p>
              <p className="text-sm">Click on a vehicle to view details and assign rides</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
